
import { UserRole } from '@/context/auth/types';
import { getRoleFromJWT } from './jwt-roles';

/**
 * Attempts to recover from a user role error
 * Provides a reliable fallback mechanism when profile data can't be retrieved
 */
export async function getReliableUserRole(userId: string): Promise<UserRole> {
  try {
    // 1. Try to get role from JWT first (most reliable)
    const jwtRole = await getRoleFromJWT();
    if (jwtRole) {
      console.log('Using JWT role as reliable source:', jwtRole);
      return jwtRole;
    }
    
    // 2. Try direct DB query if needed
    console.log('No JWT role found, trying direct DB query');
    const { supabase } = await import('@/integrations/supabase/client');
    
    // Try the new view first
    const { data: viewData } = await supabase
      .from('user_profiles_with_role')
      .select('effective_role')
      .eq('id', userId)
      .single();
      
    if (viewData?.effective_role) {
      const roleFromView = viewData.effective_role;
      console.log('Got role from view:', roleFromView);
      
      // Convert string to enum
      switch(roleFromView) {
        case 'super_admin': return UserRole.SUPER_ADMIN;
        case 'org_admin': return UserRole.ORG_ADMIN;
        case 'rest_admin': 
        case 'admin':
        case 'owner':
        case 'manager': return UserRole.REST_ADMIN;
        default: return UserRole.USER;
      }
    }
    
    // 3. Last resort, assume base user role
    console.log('Failed to get reliable role, defaulting to USER');
    return UserRole.USER;
  } catch (error) {
    console.error('Error in getReliableUserRole:', error);
    return UserRole.USER; // Final fallback
  }
}
